import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { usePersonImages } from '@/api/queries';
import { ImageGallery } from '@/features/persons/images/gallery';

/**
 * A record's photographs, to look at.
 *
 * **This is the whole of what a record does with a photograph, and the absence is the
 * specification.** Adding one, replacing one and taking one away are changes to a person, and every
 * change to a person is made on the amendment screen; a view that also wrote would put a
 * destructive control on the screen an operator has open while somebody stands in front of them.
 * So this mounts none of the three image mutations — not disabled ones, none — and passes no
 * `onRemove` to the gallery.
 *
 * **What it deliberately keeps is the faces.** The photograph is the primary control against
 * somebody using another person's band: at the door the operator compares the face in front of them
 * with the one on screen, and the full image is one click away from here. Moving the pictures behind
 * "amend" to tidy the screen would have taken the check away from the only screen that performs it.
 */
export function PersonPhotographs({ personId }: Readonly<{ personId: string }>): ReactNode {
  const { t } = useTranslation();
  const { images } = usePersonImages(personId);

  return (
    <section className="flex flex-col gap-4" data-testid="photographs">
      <h3 className="text-lg font-semibold tracking-tight">{t('person.images.title')}</h3>
      <ImageGallery personId={personId} images={images} />
    </section>
  );
}
